/**
 * The app shell: sidebar and the scrolling content area.
 *
 * The shell adds no padding. Its old p-6 never reached the browser, so every
 * page brought its own; a shell padding would double it. The Page primitive
 * gives a migrated page the standard padding.
 */
import { useRef } from "react";
import { useLocation } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import { ScrollContainerContext } from "./ScrollContainerContext";
import { useSidebarCollapsed } from "./useSidebarCollapsed";
import { useSidebarPeek } from "./useSidebarPeek";
import { size } from "../theme/tokens";
import { cn } from "../utils/cn";
import styles from "./MainLayout.module.css";

export default function MainLayout({ children }) {
  const { pathname } = useLocation();
  const { collapsed, toggle } = useSidebarCollapsed(pathname);
  const { peeking, handlers, close } = useSidebarPeek({ enabled: collapsed });
  const scrollRef = useRef(null);
  const fullBleed = pathname === "/map";
  // The sidebar and anything placed beside it with position: fixed (Map's
  // category panel) read the sidebar's width from here.
  const sidebarWidth = `${collapsed ? size.sidebarRail : size.sidebar}px`;

  return (
    <div
      data-testid="app-shell"
      className={styles.shell}
      style={{ "--sidebar-width": sidebarWidth, "--sidebar-open-width": `${size.sidebar}px` }}
    >
      {/* The rail holds its width; a peeking sidebar floats out of it over the page. */}
      <div data-testid="app-rail" className={styles.rail} {...handlers}>
        <Sidebar
          collapsed={collapsed && !peeking}
          floating={collapsed && peeking}
          onToggle={toggle}
          onNavigate={close}
        />
      </div>
      <div
        ref={scrollRef}
        data-testid="app-scroll"
        className={cn(styles.scroll, fullBleed && styles.scrollLocked)}
      >
        <ScrollContainerContext.Provider value={scrollRef}>
          <div
            data-testid="app-page"
            data-full-bleed={String(fullBleed)}
            className={fullBleed ? styles.fullBleed : styles.page}
          >
            {children}
          </div>
        </ScrollContainerContext.Provider>
      </div>
    </div>
  );
}
